import {
  ACCOUNT_PAYMENT_PROVIDER_FEE_CLASS,
  AllocationLine,
  countDecimals,
  DateTime,
  formatCalendarDateDayMonth,
  getInvoiceAmountFromLinesTotal,
  getPaymentCommunications,
  getPaymentDate,
  getPaymentDraftContext,
  getPostingAmountFromInvoice,
  getPostingsWithAmountLeftToPay,
  getValidProposedAllocation,
  hasProposedDraftBehindOpenDebt,
  type File as FileModel,
  Invoice,
  InvoiceType,
  formatNumberToEuroCurrency,
  Language,
  METRIC,
  MimeTypes,
  InvoiceWithPostings,
  isInvoiceDocumentDisputed,
  isInvoiceInTransit,
  isInstantAfter,
  isInvoicePostingSetComplete,
  isManualDraftInvoice,
  isNegativeAmountInvoice,
  toInvoiceWithPostings,
  isNilOrEmpty,
  isPostingAllocatableUnderDispute,
  isPostingReversalOrCancelledByReversal,
  LeaseExtended,
  LeasePaymentPreCheckOutcome,
  LeaseRemittanceConfiguration,
  parseLocalizedAmount,
  Posting,
  PostingForeignType,
  PostingWithAmountSelected,
  proposeLeasePaymentPreCheck,
  ReconciliationReasonCode,
  roundAtSecondDecimal,
  toCalendarDate,
  Transaction,
  TransactionStatus,
  TransactionWithAmountLinked,
  today,
  withAwaitingPostingReason,
} from '@rentguru/commons-utils';
import compact from 'lodash/compact';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';
import sumBy from 'lodash/sumBy';
import uniq from 'lodash/uniq';

import { getTransactionLeaseIds } from 'src/components/Accounting/Transactions/transactionTargetUtils';

import {
  getAmountOfSelection,
  getReconciliationType,
  isPostingOfNegativeAmountInvoice,
  ReconciliationType,
} from './reconciliationsUtils';

export interface ReconciliationPreCheck {
  reasonCodes: ReconciliationReasonCode[];
  invoiceIds: string[];
  alternatives: PostingWithAmountSelected[][];
  alternativeIndex: number | null;
}

export interface ReconciliationObject {
  lease: LeaseExtended;
  originalInvoicesOfLease: InvoiceWithPostings[];
  originalTransactionsOfLease: Transaction[];
  transactionsToReconcile: TransactionWithAmountLinked[];
  transactionsReconciled: TransactionWithAmountLinked[];
  invoicesToReconcile: InvoiceWithPostings[];
  positiveTransactionSelected: TransactionWithAmountLinked | null;
  negativeTransactionSelected: TransactionWithAmountLinked | null;
  postingsSelected: PostingWithAmountSelected[];
  pendingBalance: {
    amount: number;
    isChecked: boolean;
  };
  preCheck: ReconciliationPreCheck | null;
  invalidPostingIds: string[];
  disabledPostingIds: string[];
  disabledTransactionIds: string[];
  disabledPendingBalance: boolean;
}

export enum ReconciliationSourceKind {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  TRANSACTION_PAIR = 'TRANSACTION_PAIR',
  PENDING_BALANCE = 'PENDING_BALANCE',
  NONE = 'NONE',
}

export const getReconciliationSourceKind = ({
  positiveTransactionSelected,
  negativeTransactionSelected,
  pendingBalance,
}: Pick<
  ReconciliationObject,
  'positiveTransactionSelected' | 'negativeTransactionSelected' | 'pendingBalance'
>): ReconciliationSourceKind => {
  if (!isNil(positiveTransactionSelected) && !isNil(negativeTransactionSelected)) {
    return ReconciliationSourceKind.TRANSACTION_PAIR;
  }
  if (!isNil(positiveTransactionSelected)) {
    return ReconciliationSourceKind.INCOMING;
  }
  if (!isNil(negativeTransactionSelected)) {
    return ReconciliationSourceKind.OUTGOING;
  }
  if (pendingBalance.isChecked) {
    return ReconciliationSourceKind.PENDING_BALANCE;
  }
  return ReconciliationSourceKind.NONE;
};

export const getSourceKindsBlockedBySelectedDocuments = (
  values: Pick<
    ReconciliationObject,
    | 'positiveTransactionSelected'
    | 'negativeTransactionSelected'
    | 'pendingBalance'
    | 'postingsSelected'
    | 'invoicesToReconcile'
  >
): ReconciliationSourceKind[] => {
  if (getReconciliationSourceKind(values) !== ReconciliationSourceKind.NONE) {
    return [];
  }
  const { postingsSelected, invoicesToReconcile } = values;
  const [negativeDocumentPostings, positiveDocumentPostings] = partition(postingsSelected, (posting) =>
    isPostingOfNegativeAmountInvoice(posting, invoicesToReconcile)
  );
  return [
    ...(isEmpty(negativeDocumentPostings)
      ? []
      : [ReconciliationSourceKind.INCOMING, ReconciliationSourceKind.PENDING_BALANCE]),
    ...(isEmpty(positiveDocumentPostings) ? [] : [ReconciliationSourceKind.OUTGOING]),
  ];
};

export enum DocumentUnavailabilityReason {
  IN_TRANSIT = 'IN_TRANSIT',
  PAID = 'PAID',
  DISPUTED = 'DISPUTED',
  CANNOT_SETTLE_INCOMING = 'CANNOT_SETTLE_INCOMING',
  CANNOT_SETTLE_OUTGOING = 'CANNOT_SETTLE_OUTGOING',
  CANNOT_SETTLE_PENDING_BALANCE = 'CANNOT_SETTLE_PENDING_BALANCE',
  TRANSACTIONS_OFFSET = 'TRANSACTIONS_OFFSET',
}

export const getDocumentUnavailabilityReason = (
  invoice: InvoiceWithPostings,
  sourceKind: ReconciliationSourceKind
): DocumentUnavailabilityReason | null => {
  if (isInvoiceInTransit(invoice)) {
    return DocumentUnavailabilityReason.IN_TRANSIT;
  }
  if (invoice.paid) {
    return DocumentUnavailabilityReason.PAID;
  }
  if (isInvoiceDocumentDisputed(invoice)) {
    return DocumentUnavailabilityReason.DISPUTED;
  }
  const isNegativeDocument = isNegativeAmountInvoice(invoice, invoice.postings);
  switch (sourceKind) {
    case ReconciliationSourceKind.INCOMING:
      return isNegativeDocument ? DocumentUnavailabilityReason.CANNOT_SETTLE_INCOMING : null;
    case ReconciliationSourceKind.OUTGOING:
      return isNegativeDocument ? null : DocumentUnavailabilityReason.CANNOT_SETTLE_OUTGOING;
    case ReconciliationSourceKind.PENDING_BALANCE:
      return isNegativeDocument ? DocumentUnavailabilityReason.CANNOT_SETTLE_PENDING_BALANCE : null;
    case ReconciliationSourceKind.TRANSACTION_PAIR:
      return DocumentUnavailabilityReason.TRANSACTIONS_OFFSET;
    case ReconciliationSourceKind.NONE:
    default:
      return null;
  }
};

export const getInvoicePreselection = (
  invoicesToReconcile: InvoiceWithPostings[],
  invoiceId?: string
): PostingWithAmountSelected[] => {
  const invoice = invoicesToReconcile.find(({ id }) => id === invoiceId);
  if (isNil(invoice) || !isNil(getDocumentUnavailabilityReason(invoice, ReconciliationSourceKind.NONE))) {
    return [];
  }
  const openPostings = getPostingsWithAmountLeftToPay(invoice.postings, false) as Posting[];
  return openPostings.reduce<PostingWithAmountSelected[]>((selection, openPosting) => {
    if (openPosting.totalAmount === 0 || !isPostingAllocatableUnderDispute(openPosting, invoice)) {
      return selection;
    }
    const originalPosting = invoice.postings.find(({ id }) => id === openPosting.id) ?? openPosting;
    selection.push({ ...originalPosting, amountSelected: openPosting.totalAmount });
    return selection;
  }, []);
};

export enum DocumentStatus {
  DISPUTED = 'DISPUTED',
  INCOMPATIBLE = 'INCOMPATIBLE',
  IN_TRANSIT = 'IN_TRANSIT',
}

export const getDocumentStatus = (unavailabilityReason: DocumentUnavailabilityReason | null): DocumentStatus | null => {
  switch (unavailabilityReason) {
    case DocumentUnavailabilityReason.DISPUTED:
      return DocumentStatus.DISPUTED;
    case DocumentUnavailabilityReason.IN_TRANSIT:
      return DocumentStatus.IN_TRANSIT;
    case DocumentUnavailabilityReason.PAID:
    case null:
      return null;
    default:
      return DocumentStatus.INCOMPATIBLE;
  }
};

// The engine would accept a signed mix of invoice and credit note, which the user cannot tick
export const getPreCheckPayableInvoices = (
  invoices: InvoiceWithPostings[],
  sourceKind: ReconciliationSourceKind
): InvoiceWithPostings[] =>
  invoices.filter(
    (invoice) =>
      isNil(getDocumentUnavailabilityReason(invoice, sourceKind)) &&
      invoice.amount !== 0 &&
      isInvoicePostingSetComplete(invoice)
  );

export const keepPostingsAvailableForSource = (
  postingsSelected: PostingWithAmountSelected[],
  invoices: InvoiceWithPostings[],
  sourceKind: ReconciliationSourceKind
): PostingWithAmountSelected[] =>
  postingsSelected.filter((posting) => {
    const invoice = invoices.find(({ id }) => id === posting.invoiceId);
    return isNil(invoice) || isNil(getDocumentUnavailabilityReason(invoice, sourceKind));
  });

const toPostingsSelection = (lines: AllocationLine[], invoices: InvoiceWithPostings[]): PostingWithAmountSelected[] =>
  lines.map((line) => {
    const originalPosting =
      invoices.find((invoice) => invoice.id === line.invoiceId)?.postings.find(({ id }) => id === line.posting.id) ??
      line.posting;
    return { ...originalPosting, amountSelected: line.amountSelected };
  });

const AWS_DATE_LENGTH = 10;

export interface PreCheckSelection {
  postingsSelected: PostingWithAmountSelected[];
  preCheck: ReconciliationPreCheck | null;
}

const buildPreCheck = (
  reasonCodes: ReconciliationReasonCode[],
  invoiceIds: string[],
  alternatives: PostingWithAmountSelected[][] = []
): ReconciliationPreCheck => ({ reasonCodes, invoiceIds, alternatives, alternativeIndex: null });

// Same first reading as the fast validation of the inbox, as long as every proposed line is still open
const getProposedSelection = (
  transaction: TransactionWithAmountLinked,
  lease: ReconciliationLease,
  payableInvoices: InvoiceWithPostings[]
): PreCheckSelection | null => {
  const proposedAllocation = getValidProposedAllocation(transaction);
  if (
    isNil(proposedAllocation) ||
    proposedAllocation.leaseId !== lease.id ||
    proposedAllocation.amount !== transaction.amountToBeReconciled ||
    hasProposedDraftBehindOpenDebt(
      proposedAllocation.invoiceIds,
      payableInvoices,
      getPaymentDraftContext({
        transaction,
        lease,
        invoices: payableInvoices,
        amount: transaction.amountToBeReconciled,
      })
    )
  ) {
    return null;
  }
  const openLines = payableInvoices.flatMap((invoice) =>
    getPostingsWithAmountLeftToPay(invoice.postings, true)
      .filter((openPosting) => isPostingAllocatableUnderDispute(openPosting, invoice))
      .map((openPosting) => ({
        posting: invoice.postings.find(({ id }) => id === openPosting.id) ?? openPosting,
        amountLeftToPay: openPosting.totalAmount,
      }))
  );
  const postingsSelected = proposedAllocation.postings.map(({ id, amountSelected }) => {
    const openLine = openLines.find(({ posting }) => posting.id === id);
    if (isNil(openLine) || Math.abs(amountSelected) > Math.abs(openLine.amountLeftToPay)) {
      return null;
    }
    return { ...openLine.posting, amountSelected };
  });
  if (postingsSelected.some(isNil)) {
    return null;
  }
  return {
    postingsSelected: compact(postingsSelected),
    preCheck: buildPreCheck(
      withAwaitingPostingReason(
        [ReconciliationReasonCode.EXACT_DOCUMENTS],
        proposedAllocation.invoiceIds,
        payableInvoices
      ),
      proposedAllocation.invoiceIds
    ),
  };
};

export type ReconciliationLease = Pick<LeaseExtended, 'id'> & LeaseRemittanceConfiguration;

export const buildPreCheckSelection = (
  transaction: TransactionWithAmountLinked,
  lease: ReconciliationLease,
  invoices: InvoiceWithPostings[]
): PreCheckSelection => {
  const statementDate = transaction.statementDate?.slice(0, AWS_DATE_LENGTH);
  const sourceKind =
    transaction.amountToBeReconciled > 0 ? ReconciliationSourceKind.INCOMING : ReconciliationSourceKind.OUTGOING;
  // The screen negates every tenant-owed document, the engine only negates credit notes itself
  const payableInvoices = getPreCheckPayableInvoices(invoices, sourceKind).map((invoice) => ({
    ...invoice,
    amount: invoice.creditNote ? Math.abs(invoice.amount) : invoice.amount,
  }));
  const proposedSelection = getProposedSelection(transaction, lease, payableInvoices);
  if (!isNil(proposedSelection)) {
    return proposedSelection;
  }
  const result = proposeLeasePaymentPreCheck({
    amount: transaction.amountToBeReconciled,
    communications: getPaymentCommunications(transaction),
    lease,
    referenceDate: isNil(statementDate) ? today() : toCalendarDate(statementDate),
    paymentDate: getPaymentDate(transaction),
    payableInvoices,
  });
  switch (result.outcome) {
    case LeasePaymentPreCheckOutcome.PROPOSED:
      return {
        postingsSelected: toPostingsSelection(result.lines, payableInvoices),
        preCheck: buildPreCheck(result.reasonCodes, result.invoiceIds),
      };
    case LeasePaymentPreCheckOutcome.AMBIGUOUS:
      return {
        postingsSelected: [],
        preCheck: buildPreCheck(
          [result.reasonCode],
          uniq(result.alternatives.flat().map((line) => line.invoiceId)),
          result.alternatives.map((lines) => toPostingsSelection(lines, payableInvoices))
        ),
      };
    case LeasePaymentPreCheckOutcome.NONE:
    default:
      return { postingsSelected: [], preCheck: null };
  }
};

export const isAllocatingAmountValid = (amount: number, amountLeftToPay: number): boolean => {
  if (countDecimals(amount) > 2) {
    return false;
  }
  if (amountLeftToPay < 0) {
    return amount < 0 && amount >= amountLeftToPay;
  }
  return amount > 0 && amount <= amountLeftToPay;
};

export const parseAllocatingAmount = (rawValue: string, language: Language, amountLeftToPay: number): number | null => {
  const parsedAmount = parseLocalizedAmount(rawValue, language);
  if (isNil(parsedAmount) || !isAllocatingAmountValid(parsedAmount, amountLeftToPay)) {
    return null;
  }
  return parsedAmount;
};

export enum RecapNotice {
  SELECT_SOURCE = 'selectSource',
  SELECT_DOCUMENTS = 'selectDocuments',
  FULLY_ALLOCATED = 'fullyAllocated',
  REMAINING_TO_PENDING_BALANCE = 'remainingToPendingBalance',
  ABNORMAL_OVERPAYMENT = 'abnormalOverpayment',
  OVER_ALLOCATED = 'overAllocated',
  REMAINING_STAYS_OPEN = 'remainingStaysOpen',
  INVALID_AMOUNT = 'invalidAmount',
  CREDIT_NOTE_NETTING = 'creditNoteNetting',
  CREDIT_NOTE_CLASS_MISMATCH = 'creditNoteClassMismatch',
  TRANSACTIONS_OFFSET = 'transactionsOffset',
  PENDING_BALANCE_ALLOCATION = 'pendingBalanceAllocation',
}

export interface ReconciliationRecap {
  sourceAmount: number;
  allocatedAmount: number;
  remainingAmount: number;
  selectedLinesCount: number;
  selectedDocumentsCount: number;
  reconciliationType: ReconciliationType;
  canReconcile: boolean;
  notices: RecapNotice[];
}

const getAmountSelectedForClass = (postings: PostingWithAmountSelected[], targetClass: number) =>
  sumBy(
    postings.filter((posting) => posting.class === targetClass),
    'amountSelected'
  );

export const isCreditNoteSelectionMismatched = (postingsSelected: PostingWithAmountSelected[]): boolean => {
  const creditNotePostings = postingsSelected.filter((posting) => posting.invoice?.creditNote);
  const otherPostings = postingsSelected.filter((posting) => !posting.invoice?.creditNote);
  if (isEmpty(creditNotePostings) || isEmpty(otherPostings)) {
    return false;
  }
  const creditNoteClasses = [...new Set(creditNotePostings.map((posting) => posting.class))];
  if (otherPostings.some((posting) => !creditNoteClasses.includes(posting.class))) {
    return true;
  }
  return creditNoteClasses.some(
    (accountClass) =>
      Math.abs(getAmountSelectedForClass(creditNotePostings, accountClass)) >
      Math.abs(getAmountSelectedForClass(otherPostings, accountClass))
  );
};

export const getSelectedTransactionsAmount = ({
  positiveTransactionSelected,
  negativeTransactionSelected,
}: Pick<ReconciliationObject, 'positiveTransactionSelected' | 'negativeTransactionSelected'>): number =>
  getAmountOfSelection([], positiveTransactionSelected, negativeTransactionSelected);

const getSourceAmount = (values: ReconciliationObject): number =>
  roundAtSecondDecimal(
    getSelectedTransactionsAmount(values) + (values.pendingBalance.isChecked ? values.pendingBalance.amount : 0)
  );

const getRemainingNotices = (
  sourceKind: ReconciliationSourceKind,
  directionalRemaining: number,
  overpaymentCapAmount: number
): RecapNotice[] => {
  if (directionalRemaining === 0) {
    return [RecapNotice.FULLY_ALLOCATED];
  }
  if (directionalRemaining < 0) {
    return [RecapNotice.OVER_ALLOCATED];
  }
  if (sourceKind !== ReconciliationSourceKind.INCOMING) {
    return [RecapNotice.REMAINING_STAYS_OPEN];
  }
  return directionalRemaining > overpaymentCapAmount
    ? [RecapNotice.REMAINING_TO_PENDING_BALANCE, RecapNotice.ABNORMAL_OVERPAYMENT]
    : [RecapNotice.REMAINING_TO_PENDING_BALANCE];
};

const getSourceNotices = (
  sourceKind: ReconciliationSourceKind,
  reconciliationType: ReconciliationType,
  hasSelection: boolean
): RecapNotice[] => {
  if (sourceKind === ReconciliationSourceKind.TRANSACTION_PAIR) {
    return [RecapNotice.TRANSACTIONS_OFFSET];
  }
  if (sourceKind === ReconciliationSourceKind.NONE) {
    return reconciliationType === ReconciliationType.INVOICES
      ? [RecapNotice.CREDIT_NOTE_NETTING]
      : [RecapNotice.SELECT_SOURCE];
  }
  if (!hasSelection) {
    return [RecapNotice.SELECT_DOCUMENTS];
  }
  return sourceKind === ReconciliationSourceKind.PENDING_BALANCE ? [RecapNotice.PENDING_BALANCE_ALLOCATION] : [];
};

export const getReconciliationRecap = (
  values: ReconciliationObject,
  overpaymentCapAmount: number
): ReconciliationRecap => {
  const {
    postingsSelected,
    positiveTransactionSelected,
    negativeTransactionSelected,
    invoicesToReconcile,
    pendingBalance,
    invalidPostingIds,
  } = values;
  const sourceKind = getReconciliationSourceKind(values);
  const validPostingsSelected = postingsSelected.filter(({ id }) => !invalidPostingIds.includes(id));
  const amountLeftToAssign = getAmountOfSelection(
    validPostingsSelected,
    positiveTransactionSelected,
    negativeTransactionSelected
  );
  const remainingAmount = roundAtSecondDecimal(
    amountLeftToAssign + (pendingBalance.isChecked ? pendingBalance.amount : 0)
  );
  const shouldAddToPendingBalance =
    !isNil(positiveTransactionSelected) &&
    positiveTransactionSelected.amountToBeReconciled > sumBy(postingsSelected, 'amountSelected');
  const reconciliationType = getReconciliationType(
    postingsSelected,
    positiveTransactionSelected,
    negativeTransactionSelected,
    invoicesToReconcile,
    pendingBalance,
    shouldAddToPendingBalance
  );
  const positiveSelectedWithNegativeInvoice =
    !isNil(positiveTransactionSelected) &&
    postingsSelected.some((posting) => isPostingOfNegativeAmountInvoice(posting, invoicesToReconcile));
  const negativeSelectedWithPositiveInvoice =
    isNil(positiveTransactionSelected) &&
    !isNil(negativeTransactionSelected) &&
    postingsSelected.some((posting) => !isPostingOfNegativeAmountInvoice(posting, invoicesToReconcile));
  const pendingBalanceWithNegativeDocumentSelected =
    pendingBalance.isChecked &&
    postingsSelected.some((posting) => isPostingOfNegativeAmountInvoice(posting, invoicesToReconcile));
  const hasInvalidAmount = !isEmpty(invalidPostingIds);
  const sourceAmount = getSourceAmount(values);
  // A positive remainder on an outgoing source is an over-allocation, like a negative one on an incoming source
  const directionalRemaining = sourceAmount < 0 ? -remainingAmount : remainingAmount;
  const canReconcile =
    reconciliationType !== ReconciliationType.NONE &&
    directionalRemaining >= 0 &&
    !positiveSelectedWithNegativeInvoice &&
    !negativeSelectedWithPositiveInvoice &&
    !pendingBalanceWithNegativeDocumentSelected &&
    !hasInvalidAmount;

  const hasSelection = !isEmpty(postingsSelected);
  const isAllocating =
    hasSelection &&
    sourceKind !== ReconciliationSourceKind.NONE &&
    sourceKind !== ReconciliationSourceKind.TRANSACTION_PAIR;
  const notices = [
    ...(hasInvalidAmount ? [RecapNotice.INVALID_AMOUNT] : []),
    ...getSourceNotices(sourceKind, reconciliationType, hasSelection),
    ...(isAllocating && !hasInvalidAmount
      ? getRemainingNotices(sourceKind, directionalRemaining, overpaymentCapAmount)
      : []),
    ...(isCreditNoteSelectionMismatched(postingsSelected) ? [RecapNotice.CREDIT_NOTE_CLASS_MISMATCH] : []),
  ];

  return {
    sourceAmount,
    allocatedAmount: roundAtSecondDecimal(sumBy(validPostingsSelected, 'amountSelected')),
    remainingAmount,
    selectedLinesCount: postingsSelected.length,
    selectedDocumentsCount: new Set(compact(postingsSelected.map((posting) => posting.invoiceId))).size,
    reconciliationType,
    canReconcile,
    notices,
  };
};

export enum SettlementKind {
  TRANSACTION = 'TRANSACTION',
  CREDIT_NOTE = 'CREDIT_NOTE',
  INVOICE = 'INVOICE',
  VARIOUS_OPERATION = 'VARIOUS_OPERATION',
}

export interface DocumentSettlement {
  transaction: TransactionWithAmountLinked;
  kind: SettlementKind;
  amount: number;
}

export const getSettlementKind = (transaction: TransactionWithAmountLinked): SettlementKind => {
  if (transaction.status !== TransactionStatus.IGNORE) {
    return SettlementKind.TRANSACTION;
  }
  return transaction.postings?.some((posting) => posting.invoice?.creditNote)
    ? SettlementKind.CREDIT_NOTE
    : SettlementKind.VARIOUS_OPERATION;
};

const isSettlingDocument = (kind: SettlementKind, { creditNote, type }: Invoice): boolean => {
  switch (kind) {
    case SettlementKind.CREDIT_NOTE:
      return creditNote ?? false;
    case SettlementKind.VARIOUS_OPERATION:
      return type === InvoiceType.VARIOUS_OPERATION;
    default:
      return false;
  }
};

export const getDocumentSettlements = (
  invoice: InvoiceWithPostings,
  transactionsReconciled: TransactionWithAmountLinked[]
): DocumentSettlement[] => {
  const invoicePostingIds = new Set(invoice.postings.map((posting) => posting.id));
  const chronologicalTransactions = orderBy(transactionsReconciled, (transaction) => transaction.statementDate, [
    'asc',
  ]);
  return chronologicalTransactions.reduce<DocumentSettlement[]>((acc, transaction) => {
    const settlingPostings = (transaction.postings ?? []).filter(
      (posting) => !isNil(posting.invoicePostingId) && invoicePostingIds.has(posting.invoicePostingId)
    );
    if (!isEmpty(settlingPostings)) {
      const settlementKind = getSettlementKind(transaction);
      acc.push({
        transaction,
        kind: isSettlingDocument(settlementKind, invoice) ? SettlementKind.INVOICE : settlementKind,
        amount: roundAtSecondDecimal(sumBy(settlingPostings, 'totalAmount')),
      });
    }
    return acc;
  }, []);
};

// A remainder moved to the pending balance is reconciled through that balance, never again as a source
export const excludeTransactionsInPendingBalance = (
  transactions: TransactionWithAmountLinked[],
  pendingBalancePostings: Pick<Posting, 'leaseId' | 'foreignId'>[],
  leaseId: string
): TransactionWithAmountLinked[] => {
  const transactionIdsInPendingBalance = new Set(
    compact(pendingBalancePostings.map((posting) => (posting.leaseId === leaseId ? posting.foreignId : null)))
  );
  return transactions.filter(
    (transaction) =>
      transaction.status !== TransactionStatus.IGNORE && !transactionIdsInPendingBalance.has(transaction.id)
  );
};

export const splitTransactionsByDirection = (
  transactions: TransactionWithAmountLinked[]
): { incoming: TransactionWithAmountLinked[]; outgoing: TransactionWithAmountLinked[] } => {
  const sortedTransactions = orderBy(transactions, (transaction) => transaction.statementDate, ['desc']);
  return {
    incoming: sortedTransactions.filter((transaction) => transaction.amountToBeReconciled > 0),
    outgoing: sortedTransactions.filter((transaction) => transaction.amountToBeReconciled < 0),
  };
};

export const attachLeasePostingsToInvoices = (invoices: Invoice[], leasePostings: Posting[]): InvoiceWithPostings[] => {
  const postingsByInvoiceId = groupBy(leasePostings, 'invoiceId');
  return invoices.map((invoice) => toInvoiceWithPostings(invoice, postingsByInvoiceId[invoice.id] ?? []));
};

export const attachLeasePostingsToTransactions = <LeaseTransaction extends Pick<Transaction, 'id'>>(
  transactions: LeaseTransaction[],
  leasePostings: Posting[]
): (LeaseTransaction & { postings: Posting[] })[] => {
  const postingsByTransactionId = groupBy(leasePostings, 'transactionId');
  return transactions.map((transaction) => ({
    ...transaction,
    postings: postingsByTransactionId[transaction.id] ?? [],
  }));
};

export const getTransactionIdsMissingFromLease = (
  leasePostings: Pick<Posting, 'transactionId'>[],
  leaseTransactions: Pick<Transaction, 'id'>[]
): string[] => {
  const knownTransactionIds = new Set(leaseTransactions.map(({ id }) => id));
  return uniq(compact(leasePostings.map(({ transactionId }) => transactionId))).filter(
    (transactionId) => !knownTransactionIds.has(transactionId)
  );
};

export interface FetchedLeaseTransaction<LeaseTransaction> {
  id: string;
  transaction: LeaseTransaction | null;
}

export const mergeFetchedLeaseTransactions = <LeaseTransaction extends Pick<Transaction, 'id' | 'links'>>(
  leaseTransactions: LeaseTransaction[],
  fetchedTransactions: FetchedLeaseTransaction<LeaseTransaction>[],
  leaseId: string
): LeaseTransaction[] => {
  const deletedIds = new Set(
    fetchedTransactions.reduce<string[]>((acc, { id, transaction }) => {
      if (isNil(transaction)) {
        acc.push(id);
      }
      return acc;
    }, [])
  );
  const fetchedById = keyBy(compact(fetchedTransactions.map(({ transaction }) => transaction)), 'id');
  const keptTransactions = leaseTransactions.reduce<LeaseTransaction[]>((acc, leaseTransaction) => {
    if (!deletedIds.has(leaseTransaction.id)) {
      acc.push({ ...leaseTransaction, ...fetchedById[leaseTransaction.id] });
    }
    return acc;
  }, []);
  const knownIds = new Set(leaseTransactions.map(({ id }) => id));
  const addedTransactions = Object.values(fetchedById).filter(
    (fetchedTransaction) =>
      !knownIds.has(fetchedTransaction.id) && getTransactionLeaseIds(fetchedTransaction).includes(leaseId)
  );
  return [...keptTransactions, ...addedTransactions];
};

export const getOffsetCounterpartIds = (postings: Pick<Posting, 'foreignType' | 'foreignId'>[]): string[] =>
  uniq(
    compact(
      postings.map(({ foreignType, foreignId }) =>
        foreignType === PostingForeignType.TRANSACTION_RECONCILIATION ? foreignId : null
      )
    )
  );

export const getInvoicesOfSelectedPostings = (
  invoices: InvoiceWithPostings[],
  postingsSelected: Pick<PostingWithAmountSelected, 'id'>[]
): InvoiceWithPostings[] => {
  const selectedPostingIds = new Set(postingsSelected.map(({ id }) => id));
  return invoices.filter(({ postings }) => postings.some(({ id }) => selectedPostingIds.has(id)));
};

export const getPendingBalancePostingsOfPartiallyReconciled = (
  leasePostings: Posting[],
  transactions: Pick<Transaction, 'id' | 'status'>[]
): Posting[] => {
  const partiallyReconciledIds = new Set(
    transactions.reduce<string[]>((acc, { id, status }) => {
      if (status === TransactionStatus.PARTIALLY_RECONCILED) {
        acc.push(id);
      }
      return acc;
    }, [])
  );
  return leasePostings.filter(
    ({ foreignType, foreignId }) =>
      foreignType === PostingForeignType.TRANSACTION_PENDING_BALANCE &&
      !isNil(foreignId) &&
      partiallyReconciledIds.has(foreignId)
  );
};

const VERSION_KEY_SEPARATOR = '|';

const toVersionKey = (...parts: (string | number | boolean | null | undefined)[]): string =>
  parts.join(VERSION_KEY_SEPARATOR);

export const getLeaseInvoiceVersions = (
  invoices: Pick<Invoice, 'id' | 'leaseId' | '_version' | 'status' | 'paid'>[],
  leaseId: string
): Record<string, string> =>
  invoices.reduce<Record<string, string>>((acc, { id, leaseId: invoiceLeaseId, _version, status, paid }) => {
    if (invoiceLeaseId === leaseId) {
      acc[id] = toVersionKey(_version, status, paid);
    }
    return acc;
  }, {});

export const getChangedInvoiceIds = (
  loadedVersions: Record<string, string>,
  previousContextInvoiceIds: string[],
  contextVersions: Record<string, string>
): string[] =>
  uniq([
    ...Object.keys(contextVersions).filter((invoiceId) => loadedVersions[invoiceId] !== contextVersions[invoiceId]),
    ...previousContextInvoiceIds.filter((invoiceId) => isNil(contextVersions[invoiceId])),
  ]);

export interface LeaseInvoicesAndPostings {
  leaseInvoices: Invoice[];
  leasePostings: Posting[];
  invoiceVersions: Record<string, string>;
}

export const toLoadedLeaseInvoices = (
  rawInvoices: Invoice[],
  leaseId: string
): Pick<LeaseInvoicesAndPostings, 'leaseInvoices' | 'invoiceVersions'> => ({
  leaseInvoices: rawInvoices.filter((invoice) => invoice.leaseId === leaseId && !isManualDraftInvoice(invoice)),
  invoiceVersions: getLeaseInvoiceVersions(rawInvoices, leaseId),
});

export const replaceLeaseInvoices = (
  { leaseInvoices, leasePostings, invoiceVersions }: LeaseInvoicesAndPostings,
  leaseId: string,
  replacedInvoiceIds: string[],
  freshRawInvoices: Invoice[],
  freshPostings: Posting[]
): LeaseInvoicesAndPostings => {
  const replacedIds = new Set(replacedInvoiceIds);
  const freshPostingIds = new Set(freshPostings.map(({ id }) => id));
  const fresh = toLoadedLeaseInvoices(freshRawInvoices, leaseId);
  return {
    leaseInvoices: [...leaseInvoices.filter(({ id }) => !replacedIds.has(id)), ...fresh.leaseInvoices],
    leasePostings: [
      ...leasePostings.filter(
        ({ id, invoiceId }) => !freshPostingIds.has(id) && (isNil(invoiceId) || !replacedIds.has(invoiceId))
      ),
      ...freshPostings,
    ],
    invoiceVersions: { ...omit(invoiceVersions, replacedInvoiceIds), ...fresh.invoiceVersions },
  };
};

export interface ReconciliationBuildRequest {
  fullReload: boolean;
  transactionIdsToRefresh: string[];
}

export const mergeBuildRequests = (
  pendingRequest: ReconciliationBuildRequest | null,
  nextRequest: ReconciliationBuildRequest
): ReconciliationBuildRequest =>
  isNil(pendingRequest)
    ? nextRequest
    : {
        fullReload: pendingRequest.fullReload || nextRequest.fullReload,
        transactionIdsToRefresh: uniq([
          ...pendingRequest.transactionIdsToRefresh,
          ...nextRequest.transactionIdsToRefresh,
        ]),
      };

export enum SnapshotLoadStrategy {
  FULL = 'FULL',
  POSTINGS = 'POSTINGS',
  INVOICES = 'INVOICES',
  NONE = 'NONE',
}

const MAX_INVOICES_REFRESHED_ONE_BY_ONE = 5;

export interface SnapshotLoadState {
  canReuseSnapshot: boolean;
  changedInvoiceIds: string[];
  leaseInvoicesFetchStartedAt: DateTime | null;
  postingsFetchedFrom: DateTime | null;
}

export const getSnapshotLoadStrategy = ({
  canReuseSnapshot,
  changedInvoiceIds,
  leaseInvoicesFetchStartedAt,
  postingsFetchedFrom,
}: SnapshotLoadState): SnapshotLoadStrategy => {
  if (!canReuseSnapshot || changedInvoiceIds.length > MAX_INVOICES_REFRESHED_ONE_BY_ONE) {
    return SnapshotLoadStrategy.FULL;
  }
  if (!isEmpty(changedInvoiceIds)) {
    return SnapshotLoadStrategy.INVOICES;
  }
  const isLeaseRefetchedSincePostings =
    !isNil(leaseInvoicesFetchStartedAt) &&
    (isNil(postingsFetchedFrom) || isInstantAfter(leaseInvoicesFetchStartedAt, postingsFetchedFrom));
  return isLeaseRefetchedSincePostings ? SnapshotLoadStrategy.POSTINGS : SnapshotLoadStrategy.NONE;
};

export const getTransactionVersions = (transactions: Pick<Transaction, 'id' | '_version'>[]): Record<string, number> =>
  transactions.reduce<Record<string, number>>((acc, { id, _version }) => {
    acc[id] = _version ?? 0;
    return acc;
  }, {});

export const areLeaseTransactionsUnchanged = (
  builtContextTransactionIds: string[],
  loadedTransactionVersions: Record<string, number>,
  currentTransactions: Pick<Transaction, 'id' | '_version'>[]
): boolean => {
  const currentIds = new Set(currentTransactions.map(({ id }) => id));
  return (
    builtContextTransactionIds.every((id) => currentIds.has(id)) &&
    currentTransactions.every(
      ({ id, _version }) => !isNil(loadedTransactionVersions[id]) && (_version ?? 0) <= loadedTransactionVersions[id]
    )
  );
};

const SETTLED_TOLERANCE = 0.005;
const MONTH_KEY_LENGTH = 7;

export enum DocumentGrouping {
  DATE = 'DATE',
  DOCUMENT = 'DOCUMENT',
}

export enum DocumentSectionKind {
  MONTH = 'MONTH',
  INVOICES = 'INVOICES',
  TENANT_OWED = 'TENANT_OWED',
}

export interface DocumentAmounts {
  total: number;
  paid: number;
  open: number;
}

export const getDocumentAmounts = (invoice: InvoiceWithPostings): DocumentAmounts => {
  const postingsById = keyBy(invoice.postings, 'id');
  const { total, open } = getPostingsWithAmountLeftToPay(invoice.postings, false).reduce<Omit<DocumentAmounts, 'paid'>>(
    (acc, line) => {
      if (line.class === ACCOUNT_PAYMENT_PROVIDER_FEE_CLASS) {
        return acc;
      }
      const lineTotal = getPostingAmountFromInvoice(postingsById[line.id] ?? line, invoice);
      acc.total += lineTotal;
      acc.open += getInvoiceAmountFromLinesTotal(lineTotal, line.totalAmount);
      return acc;
    },
    { total: 0, open: 0 }
  );
  return {
    total: roundAtSecondDecimal(total),
    paid: roundAtSecondDecimal(total - open),
    open: roundAtSecondDecimal(open),
  };
};

export interface DocumentSection {
  key: string;
  kind: DocumentSectionKind;
  documents: InvoiceWithPostings[];
  paidAmount: number;
  totalAmount: number;
}

const toDocumentSection = (
  key: string,
  kind: DocumentSectionKind,
  documents: InvoiceWithPostings[]
): DocumentSection => {
  const amounts = documents.map(getDocumentAmounts);
  return {
    key,
    kind,
    documents,
    paidAmount: roundAtSecondDecimal(sumBy(amounts, ({ paid }) => Math.abs(paid))),
    totalAmount: roundAtSecondDecimal(sumBy(amounts, ({ total }) => Math.abs(total))),
  };
};

export const groupDocumentsIntoSections = (
  invoices: InvoiceWithPostings[],
  grouping: DocumentGrouping,
  newestFirst: boolean
): DocumentSection[] => {
  const sortedInvoices = orderBy(invoices, [({ invoiceDate }) => invoiceDate], [newestFirst ? 'desc' : 'asc']);
  if (grouping === DocumentGrouping.DOCUMENT) {
    const [tenantOwedDocuments, invoiceDocuments] = partition(sortedInvoices, (invoice) =>
      isNegativeAmountInvoice(invoice, invoice.postings)
    );
    const invoicesSection = toDocumentSection(
      DocumentSectionKind.INVOICES,
      DocumentSectionKind.INVOICES,
      invoiceDocuments
    );
    const tenantOwedSection = toDocumentSection(
      DocumentSectionKind.TENANT_OWED,
      DocumentSectionKind.TENANT_OWED,
      tenantOwedDocuments
    );
    return [invoicesSection, tenantOwedSection].filter(({ documents }) => !isEmpty(documents));
  }
  const documentsByMonth = groupBy(sortedInvoices, ({ invoiceDate }) => invoiceDate.slice(0, MONTH_KEY_LENGTH));
  return Object.entries(documentsByMonth).map(([month, documents]) =>
    toDocumentSection(month, DocumentSectionKind.MONTH, documents)
  );
};

export enum BalanceTone {
  TENANT_OWES = 'TENANT_OWES',
  TENANT_IN_CREDIT = 'TENANT_IN_CREDIT',
  SETTLED = 'SETTLED',
}

export const getBalanceTone = (balance: number): BalanceTone => {
  if (Math.abs(balance) < SETTLED_TOLERANCE) {
    return BalanceTone.SETTLED;
  }
  return balance < 0 ? BalanceTone.TENANT_OWES : BalanceTone.TENANT_IN_CREDIT;
};

export const getLeaseBalanceAfterReconciliation = (balance: number, allocatedAmount: number): number =>
  roundAtSecondDecimal(balance + allocatedAmount);

export enum RemainingTone {
  SETTLED = 'SETTLED',
  LEFT_TO_ALLOCATE = 'LEFT_TO_ALLOCATE',
  OVER_ALLOCATED = 'OVER_ALLOCATED',
}

export const getRemainingTone = (remainingAmount: number, sourceAmount: number): RemainingTone => {
  const directionalRemaining = sourceAmount < 0 ? -remainingAmount : remainingAmount;
  if (directionalRemaining === 0) {
    return RemainingTone.SETTLED;
  }
  return directionalRemaining > 0 ? RemainingTone.LEFT_TO_ALLOCATE : RemainingTone.OVER_ALLOCATED;
};

export enum LinePaymentState {
  UNPAID = 'UNPAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID',
}

export const getLinePaymentState = (alreadyPaidAmount: number, amountLeftToPay: number): LinePaymentState => {
  if (amountLeftToPay === 0) {
    return LinePaymentState.PAID;
  }
  return alreadyPaidAmount === 0 ? LinePaymentState.UNPAID : LinePaymentState.PARTIALLY_PAID;
};

export const getPostingIdsToggledTogether = (
  postingId: string,
  linkedPostings: { originalPosting: Pick<Posting, 'id'>; amountSelectable: number }[],
  postingsSelected: Pick<PostingWithAmountSelected, 'id'>[]
): string[] => {
  const isPostingSelected = (id: string) => postingsSelected.some((posting) => posting.id === id);
  const isSelected = isPostingSelected(postingId);
  const toggledLinkedPostings = linkedPostings.filter(({ originalPosting, amountSelectable }) =>
    isSelected ? isPostingSelected(originalPosting.id) : amountSelectable !== 0
  );
  return [postingId, ...toggledLinkedPostings.map(({ originalPosting }) => originalPosting.id)];
};

const FULL_PERCENTAGE = 100;

export const getPercentage = (amount: number, total: number): number =>
  total === 0 ? 0 : Math.min(FULL_PERCENTAGE, (Math.abs(amount) / Math.abs(total)) * FULL_PERCENTAGE);

export const META_SEPARATOR = ' · ';

export const getInvoiceIdsWithPdf = (files: FileModel[], invoiceCategoryId: string | undefined): string[] =>
  uniq(
    files.reduce<string[]>((invoiceIds, file) => {
      if (file.categoryId === invoiceCategoryId && file.mimeType === MimeTypes.PDF && !file._deleted) {
        invoiceIds.push(file.foreignKey);
      }
      return invoiceIds;
    }, [])
  );

export const isEuroSignFirst = (language: Language): boolean =>
  formatNumberToEuroCurrency(0, language).startsWith(METRIC.EURO);

export const formatStatementDate = (date: string | null | undefined, language: Language): string | null =>
  isNilOrEmpty(date) ? null : formatCalendarDateDayMonth(toCalendarDate(date), language);

export const toQuotedCommunication = (communication: string): string => `“${communication}”`;

export interface PendingBalancePart {
  id: string;
  transaction: Transaction;
  amount: number;
  transactionAmount: number;
}

export const getPendingBalanceParts = (transactions: Transaction[]): PendingBalancePart[] => {
  const transactionsById = keyBy(transactions, 'id');
  return transactions.reduce<PendingBalancePart[]>((acc, stub) => {
    const stubPostings = stub.postings ?? [];
    const isPendingBalanceStub =
      stub.status === TransactionStatus.IGNORE &&
      stub.amount !== 0 &&
      stubPostings.some((posting) => !isPostingReversalOrCancelledByReversal(posting));
    if (!isPendingBalanceStub) {
      return acc;
    }
    const originalTransactionId = stubPostings.find(
      ({ foreignType }) => foreignType === PostingForeignType.TRANSACTION_PENDING_BALANCE
    )?.foreignId;
    const originalTransaction = isNil(originalTransactionId) ? undefined : transactionsById[originalTransactionId];
    acc.push({
      id: stub.id,
      transaction: originalTransaction ?? stub,
      amount: stub.amount,
      transactionAmount: originalTransaction?.amount ?? stub.amount,
    });
    return acc;
  }, []);
};
